import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { TFunction } from 'i18next';
import { toast } from 'react-hot-toast';

import { IApprovals } from '@/modules/settings/approvals/types';
import axiosClient from '@/shared/lib/axios';
import { IGeneral } from '@/shared/types';

/*************************************** create approvalPath ****************************************** */

export const useCreateApprovalPath = (t: TFunction<'common'>) => {
  const queryClient = useQueryClient();

  return useMutation(
    ['createApprovalPaths'],
    (data: IApprovals.ICreateApprovalPathFormSchema) =>
      axiosClient.post('/company/portalInsights/approvals/approval-path', {
        ...data,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['approvalPaths']);
        queryClient.invalidateQueries(['getHomeApprovals']);
        toast.success(t('approval-path-added-successfully'));
      },
    }
  );
};
/*************************************** get entities ****************************************** */

export const getApprovalsEntities = async (
  params: IGeneral.IPaginationParamsSchema
) => {
  const { data } = await axiosClient.get<IApprovals.IGetApprovalsEntities>(
    `/company/portalInsights/approvals/entities`,
    { params }
  );
  return {
    items: data.entities,
    currentPage: params.pageNumber,
    totalPages: Math.ceil(data.totalCount / params.pageSize),
    totalItems: data.totalCount,
    nextPage:
      params?.pageNumber * params?.pageSize < data?.totalCount
        ? params?.pageNumber + 1
        : undefined,
  };
};
export const useGetApprovalsEntities = (
  params: IGeneral.IPaginationParamsSchema
) => {
  return useInfiniteQuery({
    queryKey: ['entities'],
    queryFn: ({ pageParam }) =>
      getApprovalsEntities({
        ...params,
        pageNumber: pageParam ?? params.pageNumber,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });
};
/*************************************** approvalPaths ****************************************** */

export const getApprovalPaths = async (
  params: IApprovals.IGetApprovalPathsSchema
): Promise<IApprovals.IGetApprovalPaths> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/approvals/approval-paths`,
    {
      params,
    }
  );
  return data;
};
export const useGetApprovalPaths = (
  params: IApprovals.IGetApprovalPathsSchema
) => {
  return useQuery(['approvalPaths', params], () => getApprovalPaths(params), {
    enabled: !!params.entityId,
  });
};
/*************************************** approvalPath details****************************************** */

export const getApprovalPathDetails = async (
  params?: IApprovals.IGetApprovalPathDetailsSchema
): Promise<IApprovals.IGetApprovalPathDetails> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/approvals/approval-path/${params?.id}?entityId=${params?.entityId}`
  );
  return data;
};
export const useGetApprovalPathDetails = (
  params?: IApprovals.IGetApprovalPathDetailsSchema
) => {
  return useQuery({
    queryKey: ['approvalPathDetails', params],
    queryFn: () => getApprovalPathDetails(params),
    enabled: !!params?.entityId && !!params?.id,
  });
};
/*************************************** company owners ****************************************** */

export const getCompanyOwners = async (
  params: IApprovals.IGetCompanyOwnersSchema
): Promise<IApprovals.IGetCompanyOwners> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/approvals/company-owners`,
    {
      params: {
        pageSize: params.pageSize,
        pageNumber: params.pageNumber,
      },
    }
  );
  return data;
};
export const useGetCompanyOwners = (
  params: IApprovals.IGetCompanyOwnersSchema
) => {
  return useQuery(['companyOwners', params], () => getCompanyOwners(params), {
    enabled: params.enabled,
  });
};
/*************************************** edit approver ****************************************** */
export const useUpdateApprovers = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: IApprovals.IUpdateApproverSchema) =>
      axiosClient.put(
        '/company/portalInsights/approvals/update-approvers',
        data
      ),
    {
      onSuccess: () => queryClient.invalidateQueries(['approvalPathDetails']),
    }
  );
};
/*************************************** approvalPath job order types****************************************** */

export const getJobOrderTypes = async (): Promise<
  IApprovals.IJobOrderTypeDetailsSchema[]
> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/approvals/jobs-types`
  );
  return data;
};
export const useGetJobOrderTypes = () => {
  return useQuery(['jobOrderTypes'], () => getJobOrderTypes());
};
/*************************************** path types****************************************** */

export const getPathTypes = async (): Promise<
  IApprovals.IGetPathTypesResponse[]
> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/approvals/approval-path-types`
  );
  return data;
};
export const useGetPathTypes = () => {
  return useQuery(['pathTypes'], () => getPathTypes());
};

/*************************************** approvalPath entity crops****************************************** */

export const getCropsList = async (
  params: IApprovals.IEntityCropsParams
): Promise<IApprovals.IEntityCropsSchema> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/approvals/entity-crops`,
    {
      params,
    }
  );
  return data;
};
export const useGetCropsList = () => {
  return useMutation(['cropsList'], (params: IApprovals.IEntityCropsParams) =>
    getCropsList(params)
  );
};
/*************************************** approvalPath entity fields****************************************** */

export const getFieldsList = async (
  params: IApprovals.IEntityFieldsParams
): Promise<IApprovals.IEntityFieldsSchema> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/approvals/fields`,
    {
      params: {
        entityId: params.entityId,
        cropsIds: params?.cropsIds ? `[${params.cropsIds}]` : undefined,
      },
    }
  );
  return data;
};
export const useGetFieldsList = () => {
  return useMutation(['fieldsList'], (params: IApprovals.IEntityFieldsParams) =>
    getFieldsList(params)
  );
};

/*************************************** Get grouped fields ****************************************** */
const getFieldsByCrops = async (
  entityId: number | undefined
): Promise<IApprovals.IGroupedPlots[]> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/inventory-approval/crops-plots/${entityId}`
  );
  return data;
};

export const useGetFieldsByCrops = (entityId: number | undefined) => {
  return useQuery({
    queryKey: ['getFieldsByCrops', entityId],
    queryFn: () => getFieldsByCrops(entityId),
    enabled: !!entityId,
  });
};

/*************************************** Get categories ****************************************** */

const getCategoriesByEntity = async (
  params: IApprovals.EntityPaginationParams
) => {
  const { entityId, ...rest } = params;
  const response =
    await axiosClient.get<IApprovals.IGetCategoriesByEntityResponse>(
      `/inventory/api/v3/categories/${entityId}`,
      {
        params: rest,
      }
    );
  return {
    ...response.data,
    nextPage:
      params?.page < response?.data.totalPages ? params?.page + 1 : undefined,
  };
};

export const useGetCategoriesByEntity = (
  params: IApprovals.EntityPaginationParams
) => {
  return useInfiniteQuery({
    queryKey: ['getCategoriesByEntity', params],
    queryFn: ({ pageParam }) =>
      getCategoriesByEntity({
        ...params,
        page: pageParam ?? params.page,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!params.entityId,
  });
};

/*************************************** Add inventory Approval ****************************************** */

const addInventoryApproval = async (
  formValues: IApprovals.ICreateInventoryApprovalDTO
) => {
  const { data } = await axiosClient.post(
    `/inventory/api/v1/approval-cycle`,
    formValues
  );
  return data;
};

export const useAddInventoryApproval = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addInventoryApproval,
    onSuccess: () => {
      queryClient.invalidateQueries(['getInventoryApprovals']);
      queryClient.invalidateQueries(['getHomeApprovals']);
    },
  });
};

/*************************************** get inventory approvals ****************************************** */

const getInventoryApprovals = async (
  params: IApprovals.EntityPaginationParams
): Promise<IApprovals.IGetInventoryApprovalCyclesResponse> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/approval-cycle/entity/${params.entityId}`,
    {
      params: {
        page: params.page,
        limit: params.limit,
      },
    }
  );
  return data;
};

export const useGetInventoryApprovals = (
  params: IApprovals.EntityPaginationParams
) => {
  return useQuery({
    queryKey: ['getInventoryApprovals', params],
    queryFn: () => getInventoryApprovals(params),
    enabled: !!params.entityId,
  });
};

// get inventory approvers
const getInventoryApprovers = async (
  params: IApprovals.IGetCompanyUsersProps
) => {
  const response = await axiosClient.get<IApprovals.IGetCompanyUsersResponse>(
    `/inventory/api/v1/company-users/approvers/${params.companyId}`,
    {
      params: {
        page: params.page,
        limit: params.limit,
      },
    }
  );
  return {
    ...response.data,
    nextPage:
      params?.page < response?.data.totalPages ? params?.page + 1 : undefined,
  };
};

export const useGetInventoryApprovers = (
  params: IApprovals.IGetCompanyUsersProps
) => {
  return useInfiniteQuery({
    queryKey: ['getInventoryApprovers', params],
    queryFn: ({ pageParam }) =>
      getInventoryApprovers({
        ...params,
        page: pageParam ?? params.page,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!params.companyId && !!params.limit && !!params.page,
  });
};

//update inventory approvers

const updateInventoryApprovers = async (
  params: IApprovals.IUpdateInventoryApprovalParams
) => {
  const { data } = await axiosClient.put(
    `/inventory/api/v1/approval-cycle`,
    params
  );
  return data;
};

export const useUpdateInventoryApprovers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateInventoryApprovers,
    onSuccess: () => {
      queryClient.invalidateQueries(['getInventoryApprovals']);
    },
  });
};
